import { DEFAULT_CRYPTO_DECIMALS, ZERO_WEI } from '@kwenta/sdk/constants'
import { PositionSide } from '@kwenta/sdk/types'
import { floorNumber, formatNumber, getDisplayAsset, stripZeros } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import debounce from 'lodash/debounce'

import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import TextButton from 'components/Button/TextButton'
import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import StyledSlider from 'components/Slider/StyledSlider'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import { useTranslation } from 'react-i18next'
import { selectShowPositionModal } from 'state/app/selectors'
import { editClosePositionSizeDelta } from 'state/futures/actions'
import { selectClosePositionOrderInputs } from 'state/futures/common/selectors'
import { selectEditPositionModalInfo } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

const DEBOUNCE_DELAY = 300 // 300ms delay

type PositionSizeInput = {
	onSizeChange: (v: string) => void
	maxNativeValue: string
	title: string
	value: string
	dataTestId?: string
}

// Components
const SizeInputHeader: FC<{ title: string; position: any; onMaxClick: () => void }> = memo(
	({ title, position, onMaxClick }) => (
		<OrderSizingRow>
			<InputTitle>{title}</InputTitle>
			<div>
				<TextButton onClick={onMaxClick}>Max</TextButton>
				<Body
					type="span"
					color="primary"
				>{`${formatNumber(position?.details.size ?? 0, { suggestDecimals: true })} ${getDisplayAsset(position?.market?.asset)}`}</Body>
			</div>
		</OrderSizingRow>
	)
)

export const PositionSizeInput: FC<PositionSizeInput> = memo(
	({ maxNativeValue, value, onSizeChange, title, dataTestId }) => {
		const { position } = useAppSelector(selectEditPositionModalInfo)
		const modal = useAppSelector(selectShowPositionModal)
		const [isUserInput, setIsUserInput] = useState(false)
		const [localValue, setLocalValue] = useState(value)
		const [percentValue, setPercentValue] = useState<number>(100)

		// Update local value when prop value changes
		useEffect(() => {
			setLocalValue(value)
		}, [value])

		// Initial position size setup
		useEffect(() => {
			if (modal) {
				const posSize = stripZeros(
					wei(position?.details.size ?? 0)
						.abs()
						.toString()
				)
				if (!posSize) return
				onSizeChange(posSize)
			}
		}, [modal, position?.details.size, onSizeChange])

		const initialValue = useMemo(
			() =>
				stripZeros(
					wei(position?.details.size ?? 0)
						.abs()
						.toString(DEFAULT_CRYPTO_DECIMALS)
				),
			[position?.details.size]
		)

		// biome-ignore lint/correctness/useExhaustiveDependencies: should be in deps, linter is wrong
		const debouncedUpdate = useCallback(
			debounce((value: string) => {
				onSizeChange(value)
			}, DEBOUNCE_DELAY),
			[onSizeChange]
		)

		const handleSetMax = useCallback(() => {
			const maxValue = stripZeros(maxNativeValue)
			setLocalValue(maxValue)
			onSizeChange(maxValue)
		}, [maxNativeValue, onSizeChange])

		const onChangeValue = useCallback(
			(value: string) => {
				setLocalValue(value)
				if (position?.details.size) {
					setPercentValue((Number(value) / Number(position?.details.size)) * 100)
				}
				debouncedUpdate(value)
			},
			[position?.details.size, debouncedUpdate]
		)

		const onSelectPercent = useCallback(
			(percent: number) => {
				if (!position?.details?.size || !position.market?.asset) return

				const percentage = percent / 100
				const size =
					percentage === 1
						? wei(position.details.size).abs()
						: floorNumber(wei(position.details.size).abs().mul(percentage))

				const sizeDelta = wei(size)
				const decimals = sizeDelta.abs().eq(wei(position.details.size).abs()) ? undefined : 4
				const newValue = stripZeros(sizeDelta.toString(decimals))

				setIsUserInput(true)
				setLocalValue(newValue)
				debouncedUpdate(newValue)
			},
			[position?.details?.size, position?.market?.asset, debouncedUpdate]
		)

		const sizeValueWei = useMemo(() => {
			return !localValue || Number.isNaN(Number(localValue)) ? ZERO_WEI : wei(localValue)
		}, [localValue])

		const invalid = localValue !== '' && wei(maxNativeValue).lt(sizeValueWei.abs())

		// Cleanup debounced function
		useEffect(() => {
			return () => {
				debouncedUpdate.cancel()
			}
		}, [debouncedUpdate])

		return (
			<OrderSizingContainer>
				<div>
					<SizeInputHeader title={title} position={position} onMaxClick={handleSetMax} />
					<NumericInput
						invalid={invalid}
						dataTestId={dataTestId}
						value={
							isUserInput
								? localValue.replace('-', '')
								: localValue.replace('-', '') || initialValue
						}
						placeholder="0.00"
						onChange={(_: any, v: string) => onChangeValue(v)}
						right={<Body color="secondary">{getDisplayAsset(position?.market?.asset)}</Body>}
					/>
				</div>

				<SliderRow>
					<StyledSlider
						minValue={0}
						maxValue={100}
						step={1}
						defaultValue={100}
						value={percentValue}
						onChange={(_, newValue) => {
							setPercentValue(newValue as number)
							onSelectPercent(newValue as number)
						}}
						marks={[
							{ value: 0, label: '0' },
							{ value: 25, label: '25%' },
							{ value: 50, label: '50%' },
							{ value: 75, label: '75%' },
							{ value: 100, label: '100%' },
						]}
						valueLabelFormat={(v: number) => `${v}%`}
						$currentMark={percentValue ?? 0}
					/>
				</SliderRow>
			</OrderSizingContainer>
		)
	}
)

type ClosePositionSizeProps = {
	maxNativeValue: string
	isMobile?: boolean
}

const ClosePositionSizeInput: FC<ClosePositionSizeProps> = memo(({ maxNativeValue }) => {
	const dispatch = useAppDispatch()
	const { t } = useTranslation()
	const modal = useAppSelector(selectShowPositionModal)
	const { position } = useAppSelector(selectEditPositionModalInfo)
	const { nativeSizeDelta } = useAppSelector(selectClosePositionOrderInputs)

	const negativeValue = useMemo(
		() => position?.details.side === PositionSide.LONG,
		[position?.details.side]
	)

	const onSizeChange = useCallback(
		(value: string) => {
			const sizeDelta = negativeValue ? `-${value}` : value

			if (modal) {
				dispatch(editClosePositionSizeDelta(sizeDelta))
			}
		},
		[dispatch, negativeValue, modal]
	)

	return (
		<PositionSizeInput
			maxNativeValue={maxNativeValue}
			onSizeChange={onSizeChange}
			title={t('futures.market.trade.edit-position.closing-quantity')}
			value={nativeSizeDelta}
		/>
	)
})

const OrderSizingContainer = styled(FlexDivCol)``

const OrderSizingRow = styled(FlexDivRow)`
	width: 100%;
	align-items: center;
	margin-bottom: 8px;
	cursor: default;
`

const SliderRow = styled(FlexDivRow)`
	margin-top: 8px;
	margin-bottom: 14px;
	position: relative;
`

export default ClosePositionSizeInput
